import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SharedHeader from '../../components/shared/SharedHeader';
import ProtectedPage from '../../components/shared/ProtectedPage';
import { usePermissions } from '../../utils/usePermissions';
import { toast } from '../../components/shared/toast';
import EAccountCard from './components/EAccountCard';
import CreateEAccountModal from './components/CreateEAccountModal';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import EmptyState from '../../components/shared/EmptyState';
import en from '../../translations/en.json';
import { apiClient } from '../../api/apiClient';
import { GET_E_ACCOUNTS_URL, DELETE_E_ACCOUNT_URL } from '../../api/eAccount';
import './EAccounts.css';

function EAccounts({ activeSubTab, onNavigate }) {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const canCreateEAccounts = canCreate('eaccounts');
  const canEditEAccounts = canEdit('eaccounts');
  const canDeleteEAccounts = canDelete('eaccounts');

  const activeTab = activeSubTab === 'social-account' ? 'socialAccount' : (activeSubTab || 'emails');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [accountToEdit, setAccountToEdit] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [backendAccounts, setBackendAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const tabs = [
    { id: 'emails', label: t('eAccounts.tabs.emails') },
    { id: 'domains', label: t('eAccounts.tabs.domains') },
    { id: 'servers', label: t('eAccounts.tabs.servers') },
    { id: 'socialAccount', label: t('eAccounts.tabs.socialAccount') },
    { id: 'subscriptions', label: t('eAccounts.tabs.subscriptions') }
  ];

  // Fetch accounts from backend
  const fetchAccounts = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await apiClient(`${GET_E_ACCOUNTS_URL}?type=${activeTab}`);
      setBackendAccounts(res?.accounts || []);
    } catch (err) {
      console.error('Error fetching E-Accounts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchAccounts();
  }, [activeTab]);

  const handleAddNew = () => {
    setAccountToEdit(null);
    setShowCreateModal(true);
  };

  const handleCreateSuccess = () => {
    fetchAccounts();
  };

  const handleEditCard = (card) => {
    setAccountToEdit(card.rawAccount);
    setShowCreateModal(true);
  };

  const handleDeleteCard = (card) => {
    setAccountToDelete(card.rawAccount);
  };

  const handleConfirmDelete = async () => {
    if (!accountToDelete) return;
    try {
      await apiClient(DELETE_E_ACCOUNT_URL(accountToDelete.id), {
        method: 'DELETE'
      });
      toast.success('E-Account deleted successfully');
      setAccountToDelete(null);
      fetchAccounts();
    } catch (err) {
      console.error('Error deleting E-Account:', err);
      toast.error('Failed to delete E-Account');
    }
  };

  const formatBackendCard = (acc) => {
    const type = acc.account_type;
    let title = acc.email_name || acc.name || acc.subscription_for || acc.social_type || 'E-Account';
    let fields = [];

    if (type === 'email') {
      title = acc.email_name;
      fields = [
        { label: 'Recovery Phone', value: acc.recovery_phone },
        { label: 'Recovery Mail', value: acc.recovery_email }
      ];
    } else if (type === 'domain' || type === 'server') {
      title = acc.name;
      fields = [
        { label: 'Why was buy', value: acc.why_buy },
        { label: 'Login Mail', value: acc.account_email },
        { label: 'Renew Date', value: acc.renew_date ? String(acc.renew_date).split('T')[0] : '—' }
      ];
    } else if (type === 'social_account') {
      title = acc.social_type || 'Social Account';
      fields = [
        { label: 'Name', value: acc.name },
        { label: 'Mail', value: acc.mail }
      ];
    } else if (type === 'subscription') {
      title = acc.subscription_for || 'Subscription';
      fields = [
        { label: 'Name', value: acc.name },
        { label: 'Mail', value: acc.mail },
        { label: 'Renew Date', value: acc.renew_date ? String(acc.renew_date).split('T')[0] : '—' }
      ];
    }

    return {
      id: acc.id,
      title,
      titleIsKey: false,
      fields,
      description: acc.description,
      whoHas: acc.who_has,
      rawAccount: acc
    };
  };

  const formattedBackendCards = useMemo(() => {
    return backendAccounts.map(formatBackendCard);
  }, [backendAccounts]);

  const filteredCards = useMemo(() => {
    if (!searchQuery.trim()) return formattedBackendCards;
    const q = searchQuery.toLowerCase();
    return formattedBackendCards.filter((card) => {
      if (card.title && card.title.toLowerCase().includes(q)) return true;
      if (card.description && card.description.toLowerCase().includes(q)) return true;
      if (card.whoHas && card.whoHas.toLowerCase().includes(q)) return true;
      return card.fields.some(f => (f.value && String(f.value).toLowerCase().includes(q)));
    });
  }, [formattedBackendCards, searchQuery]);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800));

      const res = await apiClient(`${GET_E_ACCOUNTS_URL}?type=${activeTab}&export=true`);
      const accountsList = res?.accounts || [];

      if (accountsList.length === 0) {
        toast.warning('No records available to export.');
        return;
      }

      let headers = [];
      let rows = [];

      if (activeTab === 'emails') {
        headers = ['Email Name', 'Recovery Phone Number', 'Recovery Email', 'Description', 'Who Has', 'Created At'];
        rows = accountsList.map(a => [
          a.email_name || '—',
          a.recovery_phone || '—',
          a.recovery_email || '—',
          a.description || '—',
          a.who_has || '—',
          a.created_at ? String(a.created_at).split('T')[0] : '—'
        ]);
      } else if (activeTab === 'domains' || activeTab === 'servers') {
        const nameHeader = activeTab === 'domains' ? 'Domain Name' : 'Server Name';
        headers = [nameHeader, 'Why Was Buy', 'Account Email', 'Renew Date', 'Description', 'Who Has', 'Created At'];
        rows = accountsList.map(a => [
          a.name || '—',
          a.why_buy || '—',
          a.account_email || '—',
          a.renew_date ? String(a.renew_date).split('T')[0] : '—',
          a.description || '—',
          a.who_has || '—',
          a.created_at ? String(a.created_at).split('T')[0] : '—'
        ]);
      } else if (activeTab === 'socialAccount') {
        headers = ['Social Account Type', 'Name', 'Mail', 'Description', 'Who Has', 'Created At'];
        rows = accountsList.map(a => [
          a.social_type || '—',
          a.name || '—',
          a.mail || '—',
          a.description || '—',
          a.who_has || '—',
          a.created_at ? String(a.created_at).split('T')[0] : '—'
        ]);
      } else if (activeTab === 'subscriptions') {
        headers = ['Subscription For', 'Name', 'Mail', 'Renew Date', 'Description', 'Who Has', 'Created At'];
        rows = accountsList.map(a => [
          a.subscription_for || '—',
          a.name || '—',
          a.mail || '—',
          a.renew_date ? String(a.renew_date).split('T')[0] : '—',
          a.description || '—',
          a.who_has || '—',
          a.created_at ? String(a.created_at).split('T')[0] : '—'
        ]);
      }

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `e_accounts_${activeTab}_export_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('E-Accounts exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export E-Accounts.');
    } finally {
      setIsExporting(false);
    }
  };

  // Determine initial type for modal based on active tab
  const getInitialModalType = () => {
    if (activeTab === 'emails') return 'email';
    if (activeTab === 'domains') return 'domain';
    if (activeTab === 'servers') return 'server';
    if (activeTab === 'socialAccount') return 'social_account';
    if (activeTab === 'subscriptions') return 'subscription';
    return '';
  };

  return (
    <ProtectedPage module="eaccounts">
      <div className="eaccounts-page-wrapper">
        {/* Shared Header Controls */}
        <SharedHeader
          title={t('eAccounts.title')}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={t('eAccounts.searchPlaceholder')}
          resultCount={filteredCards.length}
        resultLabel={t('eAccounts.resultsCount')}
        onSortClick={() => {
          setSortOpen(!sortOpen);
          setFilterOpen(false);
        }}
        onFilterClick={() => {
          setFilterOpen(!filterOpen);
          setSortOpen(false);
        }}
        sortActive={sortOpen}
        filterActive={filterOpen}
        isSortApplied={false}
        isFilterApplied={false}
        onExportClick={handleExport}
        onAddNewClick={canCreateEAccounts ? handleAddNew : null}
        addNewText="Create E-Account"
        canAddNew={canCreateEAccounts}
      />

      {/* Sub-tabs Selectors */}
      <div className="eaccounts-tabs-row">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`eaccounts-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              const pathSegment = tab.id === 'socialAccount' ? 'social-account' : tab.id;
              onNavigate(`/e-accounts/${pathSegment}`);
              setSearchQuery(''); // reset search on tab switch
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Grid of Cards */}
      <div className="eaccounts-grid-container">
        {isLoading ? (
          <LoadingOverlay isLoading message="Fetching accounts, please wait." />
        ) : filteredCards.length > 0 ? (
          filteredCards.map((card) => (
            <EAccountCard
              key={card.id}
              account={card}
              onEdit={canEditEAccounts ? handleEditCard : null}
              onDelete={canDeleteEAccounts ? handleDeleteCard : null}
              t={t}
            />
          ))
        ) : (
          <EmptyState title="No Accounts Found" subtitle="No accounts match your search criteria." hasSearch={true} />
        )}
      </div>

      {/* Create / Edit E-Account Popup Modal */}
      {showCreateModal && (
        <CreateEAccountModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setAccountToEdit(null);
          }}
          onSuccess={handleCreateSuccess}
          initialType={getInitialModalType()}
          accountToEdit={accountToEdit}
        />
      )}

      {/* Delete Primary Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!accountToDelete}
        type="danger"
        title="Delete E-Account"
        message={`Are you sure you want to delete the e-account "${accountToDelete?.name || accountToDelete?.email_name || accountToDelete?.subscription_for || accountToDelete?.social_type || 'this account'}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setAccountToDelete(null)}
      />

      {/* Global Export Loading Overlay */}
      <LoadingOverlay isLoading={isExporting} message={t('common.exportingMessage') || 'Exporting data...'} />
      </div>
    </ProtectedPage>
  );
}

export default EAccounts;
